import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { 
  ChevronRight, ChevronLeft, Lightbulb, Target, Users, 
  Calendar, CheckCircle, FileText, MessageSquare, Rocket, Shield
} from 'lucide-react';

const ONBOARDING_STEPS = [
  {
    id: 'welcome',
    title: "Welcome to SLP Coach",
    subtitle: "Let's set you up for success",
    icon: Lightbulb,
    content: ({ onNext }) => (
      <div className="text-center py-8">
        <div className="w-20 h-20 bg-amber-100 rounded-2xl mx-auto mb-6 flex items-center justify-center">
          <Lightbulb className="h-10 w-10 text-amber-600" />
        </div>
        <h2 className="text-2xl font-bold text-slate-900 mb-3">
          Hi! I'm your Change Coach
        </h2>
        <p className="text-slate-600 mb-6 max-w-md mx-auto">
          I'll guide you through your change initiative step by step. 
          Whether you're new to change management or experienced, I'll help you 
          think through what matters and spot what you might miss.
        </p>
        <Button onClick={onNext} className="bg-amber-500 hover:bg-amber-600 text-slate-900">
          Let's get started
          <ChevronRight className="h-4 w-4 ml-2" />
        </Button>
      </div>
    )
  },
  {
    id: 'name',
    title: "What's this change about?",
    subtitle: "Give it a name we can work with",
    icon: Target,
    content: ({ data, setData, onNext, onBack }) => (
      <div className="py-6">
        <div className="mb-6">
          <label className="block text-sm font-medium text-slate-700 mb-2">
            What's your change called?
          </label>
          <Input
            value={data.name || ''}
            onChange={(e) => setData({ ...data, name: e.target.value })}
            placeholder="e.g., New CRM System, Safety Program, Office Relocation"
            className="text-lg"
            autoFocus
          />
          <p className="text-sm text-slate-500 mt-2">
            Don't worry about getting it perfect - you can always change this later
          </p>
        </div>
        <div className="flex justify-between">
          <Button variant="ghost" onClick={onBack}>
            <ChevronLeft className="h-4 w-4 mr-1" />
            Back
          </Button>
          <Button 
            onClick={onNext} 
            disabled={!data.name?.trim()}
            className="bg-amber-500 hover:bg-amber-600 text-slate-900"
          >
            Next
            <ChevronRight className="h-4 w-4 ml-2" />
          </Button>
        </div>
      </div>
    )
  },
  {
    id: 'description',
    title: "What's changing?",
    subtitle: "In simple terms, what's happening?",
    icon: FileText,
    content: ({ data, setData, onNext, onBack }) => (
      <div className="py-6">
        <div className="mb-6">
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Describe what's changing in a sentence or two
          </label>
          <Textarea
            value={data.description || ''}
            onChange={(e) => setData({ ...data, description: e.target.value })}
            placeholder="e.g., We're moving from our old payroll system to a new cloud-based one. Finance, HR and all managers will need to learn the new system."
            rows={4}
            className="resize-none"
          />
        </div>
        <div className="flex justify-between">
          <Button variant="ghost" onClick={onBack}>
            <ChevronLeft className="h-4 w-4 mr-1" />
            Back
          </Button>
          <Button 
            onClick={onNext}
            disabled={!data.description?.trim()}
            className="bg-amber-500 hover:bg-amber-600 text-slate-900"
          >
            Next
            <ChevronRight className="h-4 w-4 ml-2" />
          </Button>
        </div>
      </div>
    )
  },
  {
    id: 'who',
    title: "Who's affected?",
    subtitle: "Which teams or groups need to change?",
    icon: Users,
    content: ({ data, setData, onNext, onBack }) => (
      <div className="py-6">
        <div className="mb-6">
          <label className="block text-sm font-medium text-slate-700 mb-2">
            List the teams, roles, or groups that will be affected
          </label>
          <Textarea
            value={data.affected || ''}
            onChange={(e) => setData({ ...data, affected: e.target.value })}
            placeholder="e.g., Finance team, HR, Operations managers, Payroll administrators"
            rows={3}
            className="resize-none"
          />
          <p className="text-sm text-slate-500 mt-2">
            It's okay to be rough here - we'll dig deeper together
          </p>
        </div>
        <div className="flex justify-between">
          <Button variant="ghost" onClick={onBack}>
            <ChevronLeft className="h-4 w-4 mr-1" />
            Back
          </Button>
          <Button 
            onClick={onNext}
            className="bg-amber-500 hover:bg-amber-600 text-slate-900"
          >
            Next
            <ChevronRight className="h-4 w-4 ml-2" />
          </Button>
        </div>
      </div>
    )
  },
  {
    id: 'when',
    title: "When is this happening?",
    subtitle: "What's the timeline?",
    icon: Calendar,
    content: ({ data, setData, onNext, onBack }) => (
      <div className="py-6">
        <div className="mb-6">
          <label className="block text-sm font-medium text-slate-700 mb-2">
            When do you expect this to go live?
          </label>
          <Input
            value={data.timeline || ''}
            onChange={(e) => setData({ ...data, timeline: e.target.value })}
            placeholder="e.g., March 2025, Q2 this year, Next month"
          />
        </div>
        <div className="mb-6">
          <label className="block text-sm font-medium text-slate-700 mb-2">
            What stage are you at?
          </label>
          <div className="grid grid-cols-2 gap-3">
            {[
              { value: 'early', label: 'Early planning', desc: 'Just getting started' },
              { value: 'planning', label: 'In planning', desc: 'Working out the details' },
              { value: 'delivery', label: 'In delivery', desc: 'Building or rolling out' },
              { value: 'live', label: 'Already live', desc: 'Embedding or fixing issues' },
            ].map((stage) => (
              <button
                key={stage.value}
                onClick={() => setData({ ...data, stage: stage.value })}
                className={`p-3 border rounded-lg text-left transition-colors ${
                  data.stage === stage.value 
                    ? 'border-amber-400 bg-amber-50' 
                    : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <div className="font-medium text-slate-900">{stage.label}</div>
                <div className="text-sm text-slate-500">{stage.desc}</div>
              </button>
            ))}
          </div>
        </div>
        <div className="flex justify-between">
          <Button variant="ghost" onClick={onBack}>
            <ChevronLeft className="h-4 w-4 mr-1" />
            Back
          </Button>
          <Button 
            onClick={onNext}
            className="bg-amber-500 hover:bg-amber-600 text-slate-900"
          >
            Next
            <ChevronRight className="h-4 w-4 ml-2" />
          </Button>
        </div>
      </div>
    )
  },
  {
    id: 'documents',
    title: "Got any documents?",
    subtitle: "Optional, but helpful if you have them",
    icon: FileText,
    content: ({ data, setData, onNext, onBack, onUpload }) => (
      <div className="py-6">
        <p className="text-slate-600 mb-6">
          If you have any project documents you'd like me to review, 
          I can give you specific feedback. If not, no worries - 
          we can work through it together.
        </p>
        
        {/* Privacy notice */}
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-3">
            <Shield className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
            <div>
              <div className="font-medium text-emerald-800 mb-1">Your data stays private</div>
              <p className="text-sm text-emerald-700">
                Documents are encrypted and stored securely. Only you can see them - 
                we never share, sell, or use your data for anything else.
              </p>
            </div>
          </div>
        </div>
        
        <div className="grid grid-cols-2 gap-4 mb-6">
          <button
            onClick={() => setData({ ...data, hasDocuments: true })}
            className={`p-4 border-2 border-dashed rounded-lg text-center transition-colors ${
              data.hasDocuments === true
                ? 'border-amber-400 bg-amber-50'
                : 'border-slate-200 hover:border-slate-300'
            }`}
          >
            <FileText className="h-8 w-8 mx-auto mb-2 text-slate-400" />
            <div className="font-medium text-slate-900">Yes, I have documents</div>
            <div className="text-sm text-slate-500">I'll upload them next</div>
          </button>
          <button
            onClick={() => setData({ ...data, hasDocuments: false })}
            className={`p-4 border-2 border-dashed rounded-lg text-center transition-colors ${
              data.hasDocuments === false
                ? 'border-amber-400 bg-amber-50'
                : 'border-slate-200 hover:border-slate-300'
            }`}
          >
            <MessageSquare className="h-8 w-8 mx-auto mb-2 text-slate-400" />
            <div className="font-medium text-slate-900">No documents yet</div>
            <div className="text-sm text-slate-500">Let's just talk it through</div>
          </button>
        </div>

        <div className="flex justify-between">
          <Button variant="ghost" onClick={onBack}>
            <ChevronLeft className="h-4 w-4 mr-1" />
            Back
          </Button>
          <Button 
            onClick={onNext}
            disabled={data.hasDocuments === undefined}
            className="bg-amber-500 hover:bg-amber-600 text-slate-900"
          >
            {data.hasDocuments ? "Next - Upload documents" : "Let's go!"}
            <ChevronRight className="h-4 w-4 ml-2" />
          </Button>
        </div>
      </div>
    )
  },
  {
    id: 'ready',
    title: "You're all set!",
    subtitle: "Let's start your change journey",
    icon: Rocket,
    content: ({ data, onComplete }) => (
      <div className="text-center py-8">
        <div className="w-20 h-20 bg-emerald-100 rounded-2xl mx-auto mb-6 flex items-center justify-center">
          <CheckCircle className="h-10 w-10 text-emerald-600" />
        </div>
        <h2 className="text-2xl font-bold text-slate-900 mb-3">
          Great! Here's what I understand:
        </h2>
        <div className="bg-slate-50 rounded-lg p-4 mb-6 text-left max-w-md mx-auto">
          <div className="mb-3">
            <div className="text-sm text-slate-500">Change name</div>
            <div className="font-medium text-slate-900">{data.name}</div>
          </div>
          {data.description && (
            <div className="mb-3">
              <div className="text-sm text-slate-500">What's changing</div>
              <div className="text-slate-700">{data.description}</div>
            </div>
          )}
          {data.affected && (
            <div className="mb-3">
              <div className="text-sm text-slate-500">Who's affected</div>
              <div className="text-slate-700">{data.affected}</div>
            </div>
          )}
          {data.timeline && (
            <div>
              <div className="text-sm text-slate-500">Timeline</div>
              <div className="text-slate-700">{data.timeline}</div>
            </div>
          )}
        </div>
        <Button onClick={onComplete} className="bg-amber-500 hover:bg-amber-600 text-slate-900 h-12 px-8">
          <Rocket className="h-5 w-5 mr-2" />
          Start my change journey
        </Button>
      </div>
    )
  }
];

export default function ProjectOnboarding({ onComplete, onCancel }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [data, setData] = useState({});

  const step = ONBOARDING_STEPS[currentStep];
  const progress = ((currentStep) / (ONBOARDING_STEPS.length - 1)) * 100;

  const handleNext = () => {
    if (currentStep < ONBOARDING_STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleComplete = () => {
    onComplete(data);
  };

  const StepContent = step.content;

  return (
    <div className="fixed inset-0 bg-white z-50 flex flex-col">
      {/* Progress bar */}
      <div className="h-1 bg-slate-100">
        <motion.div 
          className="h-full bg-amber-500"
          initial={{ width: 0 }}
          animate={{ width: `${progress}%` }}
          transition={{ duration: 0.3 }}
        />
      </div>

      {/* Header */}
      <div className="p-4 border-b border-slate-100">
        <div className="max-w-xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-slate-900 rounded-lg flex items-center justify-center">
              <step.icon className="h-5 w-5 text-amber-400" />
            </div>
            <div>
              <h1 className="font-semibold text-slate-900">{step.title}</h1>
              <p className="text-sm text-slate-500">{step.subtitle}</p>
            </div>
          </div>
          {currentStep > 0 && (
            <button 
              onClick={onCancel}
              className="text-sm text-slate-500 hover:text-slate-700"
            >
              Save & exit
            </button>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-xl mx-auto px-4">
          <AnimatePresence mode="wait">
            <motion.div
              key={step.id}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.2 }}
            >
              <StepContent
                data={data}
                setData={setData}
                onNext={handleNext}
                onBack={handleBack}
                onComplete={handleComplete}
              />
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      {/* Step indicators */}
      <div className="p-4 border-t border-slate-100">
        <div className="max-w-xl mx-auto flex justify-center gap-2">
          {ONBOARDING_STEPS.map((s, i) => (
            <div
              key={s.id}
              className={`w-2 h-2 rounded-full transition-colors ${
                i === currentStep
                  ? 'bg-amber-500'
                  : i < currentStep
                  ? 'bg-emerald-400'
                  : 'bg-slate-200'
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
